import { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { AlertTriangle } from 'lucide-react';

import useApi from '../api';
import toastConfig from '../config/Toast';
import PublishAgent from './PublishAgent';
import EditorPresenceStack from './EditorPresenceStack';
import {
    AgentEditorPresence,
    dedupeEditorPresence,
    pickMostRecentEditor,
} from './presenceTypes';
import { useUser } from '../user/UserContext';

interface AgentEditorHeaderActionsProps {
    agId: string;
    draftUpdatedAt: string | null;
    onSaveBeforePublish: () => Promise<void>;
    onSaveDraft: () => Promise<void>;
    isSavingDraft: boolean;
    onDraftVersionResync?: (updatedAt: string) => void;
}

export default function AgentEditorHeaderActions({
    agId,
    draftUpdatedAt,
    onSaveBeforePublish,
    onSaveDraft,
    isSavingDraft,
    onDraftVersionResync,
}: AgentEditorHeaderActionsProps) {
    const { connectWebSocket } = useApi();
    const { user } = useUser();
    const agentPresenceSocketRef = useRef<WebSocket | null>(null);
    const draftUpdatedAtRef = useRef<string | null>(draftUpdatedAt);
    const warnedDraftUpdatedAtRef = useRef<string | null>(null);
    const agentPresenceConnectionIdRef = useRef<string | null>(null);
    const isSavingDraftRef = useRef(isSavingDraft);
    const currentUserIdRef = useRef<string | null>(user?.id ?? null);
    const currentUserEmailRef = useRef<string | null>(user?.email ?? null);
    const otherEditorsRef = useRef<AgentEditorPresence[]>([]);
    const onDraftVersionResyncRef = useRef(onDraftVersionResync);
    const [otherEditors, setOtherEditors] = useState<AgentEditorPresence[]>([]);
    const [draftChangedElsewhere, setDraftChangedElsewhere] = useState<string | null>(null);
    const [publishRefreshKey, setPublishRefreshKey] = useState(0);

    useEffect(() => {
        draftUpdatedAtRef.current = draftUpdatedAt;
        warnedDraftUpdatedAtRef.current = null;
        setDraftChangedElsewhere(null);
    }, [draftUpdatedAt]);

    useEffect(() => {
        otherEditorsRef.current = otherEditors;
    }, [otherEditors]);

    useEffect(() => {
        onDraftVersionResyncRef.current = onDraftVersionResync;
    }, [onDraftVersionResync]);

    useEffect(() => {
        isSavingDraftRef.current = isSavingDraft;
    }, [isSavingDraft]);

    useEffect(() => {
        currentUserIdRef.current = user?.id ?? null;
        currentUserEmailRef.current = user?.email ?? null;
    }, [user?.email, user?.id]);

    useEffect(() => {
        let cancelled = false;
        let heartbeatInterval: NodeJS.Timeout | null = null;

        const initializeEditorPresence = async () => {
            try {
                const ws = await connectWebSocket(`/ws/agents/${agId}/editor-presence`);
                if (!ws || cancelled) {
                    ws?.close();
                    return;
                }

                agentPresenceSocketRef.current = ws;

                const announcePresence = () => {
                    ws.send(JSON.stringify({ type: 'join' }));
                    heartbeatInterval = setInterval(() => {
                        if (ws.readyState === WebSocket.OPEN) {
                            ws.send(JSON.stringify({ type: 'heartbeat' }));
                        }
                    }, 25000);
                };

                ws.onopen = announcePresence;

                ws.onmessage = (event) => {
                    try {
                        const message = JSON.parse(event.data);
                        if (message.type === 'presence_connected') {
                            agentPresenceConnectionIdRef.current = message.connection_id || null;
                            return;
                        }

                        if (message.type === 'presence_snapshot' && Array.isArray(message.editors)) {
                            const selfConnectionId = agentPresenceConnectionIdRef.current;
                            const currentUserId = currentUserIdRef.current;
                            const currentUserEmail = currentUserEmailRef.current?.toLowerCase() ?? null;
                            const otherEditors = message.editors.filter(
                                (editor: AgentEditorPresence) => {
                                    if (!editor.connection_id) return false;
                                    if (editor.connection_id === selfConnectionId) return false;
                                    if (editor.id && editor.id === currentUserId) return false;
                                    if (
                                        editor.email &&
                                        editor.email.toLowerCase() === currentUserEmail
                                    ) {
                                        return false;
                                    }
                                    return true;
                                }
                            );
                            setOtherEditors(dedupeEditorPresence(otherEditors));
                            return;
                        }

                        if (message.type === 'agent_published') {
                            // Another session (or this one) published; refresh the
                            // unpublished-changes badge so it doesn't go stale.
                            setPublishRefreshKey((key) => key + 1);
                            return;
                        }

                        if (message.type === 'draft_saved' && typeof message.updated_at === 'string') {
                            const editorId =
                                typeof message.editor?.id === 'string' ? message.editor.id : null;
                            const isCurrentUserSave =
                                Boolean(editorId) && editorId === currentUserIdRef.current;
                            const isOwnInFlightSave =
                                isSavingDraftRef.current &&
                                isCurrentUserSave;

                            if (isOwnInFlightSave) {
                                return;
                            }

                            // A draft_saved that isn't attributable to the current user, arriving
                            // while no other human editor is connected, can't be a concurrent edit —
                            // it's an automated/system write (e.g. the initial website training
                            // writing back to a freshly created draft on a brand-new trial). Warning
                            // the solo editor that "someone else" changed their draft is a false
                            // positive that also blocks their save. Silently resync the draft version
                            // instead so their next save passes the conflict precondition.
                            const hasOtherEditors = otherEditorsRef.current.length > 0;
                            if (!isCurrentUserSave && !hasOtherEditors) {
                                warnedDraftUpdatedAtRef.current = message.updated_at;
                                draftUpdatedAtRef.current = message.updated_at;
                                onDraftVersionResyncRef.current?.(message.updated_at);
                                return;
                            }

                            if (
                                draftUpdatedAtRef.current &&
                                message.updated_at !== draftUpdatedAtRef.current &&
                                warnedDraftUpdatedAtRef.current !== message.updated_at
                            ) {
                                warnedDraftUpdatedAtRef.current = message.updated_at;
                                setDraftChangedElsewhere(message.updated_at);

                                const actor =
                                    pickMostRecentEditor(otherEditorsRef.current)?.name || 'Someone';
                                const warningMessage = isCurrentUserSave
                                    ? 'This draft was saved from another tab or session. Refresh to keep editing.'
                                    : `${actor} just saved new changes. Refresh to keep editing.`;
                                toast.warning(warningMessage, {
                                    ...toastConfig,
                                    autoClose: false,
                                    onClick: () => window.location.reload(),
                                });
                            }
                        }
                    } catch (error) {
                        console.error('Error handling agent editor presence message:', error);
                    }
                };

                ws.onclose = () => {
                    if (heartbeatInterval) {
                        clearInterval(heartbeatInterval);
                    }
                    if (agentPresenceSocketRef.current === ws) {
                        agentPresenceSocketRef.current = null;
                    }
                };

                if (ws.readyState === WebSocket.OPEN) {
                    announcePresence();
                }
            } catch (error) {
                console.error('Error connecting to agent editor presence:', error);
            }
        };

        initializeEditorPresence();

        return () => {
            cancelled = true;
            if (heartbeatInterval) {
                clearInterval(heartbeatInterval);
            }
            const ws = agentPresenceSocketRef.current;
            if (ws) {
                if (ws.readyState === WebSocket.OPEN) {
                    ws.send(JSON.stringify({ type: 'silent_close_connection' }));
                }
                ws.close();
                agentPresenceSocketRef.current = null;
            }
            agentPresenceConnectionIdRef.current = null;
        };
    }, [agId, connectWebSocket]);

    return (
        <div className="flex items-center gap-3">
            {draftChangedElsewhere && (
                <button
                    type="button"
                    onClick={() => window.location.reload()}
                    className="flex items-center gap-1 rounded-full border border-amber-500/40 bg-amber-500/10 px-2.5 py-1 text-[11px] font-medium text-amber-700 hover:bg-amber-500/20 dark:text-amber-200"
                >
                    <AlertTriangle className="h-3 w-3" />
                    Refresh required
                </button>
            )}
            <EditorPresenceStack editors={otherEditors} />
            <PublishAgent
                agId={agId}
                onSaveBeforePublish={onSaveBeforePublish}
                onSaveDraft={onSaveDraft}
                isSavingDraft={isSavingDraft}
                draftUpdatedAt={draftUpdatedAt}
                publishRefreshKey={publishRefreshKey}
            />
        </div>
    );
}
